/**
 * The persistent header: score, overs, rates, both batters, the bowler.
 *
 * Every number here comes out of `MatchState.header`, which is one field of the single
 * fold. That is not incidental — it is the reason the header cannot disagree with the
 * scorecard six inches below it (ADR-0006). A `getCurrentScore()` helper next to a
 * `getBowlingFigures()` helper is exactly how incumbents ship a header that says 143/4
 * above a card that adds up to 141.
 *
 * ## What #42 changed, and what it did not
 *
 * The crease was three lines of text — `Rohit* 10 (4)` and a right-aligned strike rate. It
 * is cards now: name, runs, balls, SR, each in its own slot. Reading a number out of a
 * sentence is slower than reading it out of a column, and this is read between deliveries.
 *
 * Added: the partnership, a phase badge, and a progress bar for overs used. All three were
 * already computable and none of them were shown, so a scorer answering "how are these two
 * going?" was counting back through the over strip.
 *
 * **Nothing here moved into the tap half, and nothing here is a control.** The whole header
 * is read-only, which is what lets it change shape between balls without breaking the rule
 * that the bottom half never does.
 */
import {
  formatBowling,
  formatOvers,
  formatRate,
  formatScore,
  strikerMark,
} from "@sportsos/engine";
import type { BattingLine, InningsState, MatchState, PlayerId } from "@sportsos/engine";
import { inningsPhase, phaseLabel } from "../match/phase.ts";

interface Props {
  readonly state: MatchState;
  readonly innings: InningsState;
  readonly name: (playerId: PlayerId) => string;
  /**
   * Who is batting, and under what rules.
   *
   * Both were already on the screen — the team names in the top bar, the format on the setup
   * screen an hour ago — and neither answered the question a scorer actually asks after
   * twenty minutes in the sun, which is *whose* 84/3 this is.
   */
  readonly battingTeamName: string;
  /** The RuleSet's name, e.g. `T20`. */
  readonly format: string;
}

export function Header({
  state,
  innings,
  name,
  battingTeamName,
  format,
}: Props): React.ReactElement {
  const header = state.header;
  if (header === null) return <article className="ds-card">Waiting for the innings to start</article>;

  const phase = inningsPhase(header.overs.completed, innings.oversAvailable);
  const partnership = header.currentPartnership;

  return (
    <article className="ds-card" style={{ padding: "var(--ds-space-4) var(--ds-space-5)" }}>
      {/* Above the score, in the read half. Fixed height whatever the names are, so nothing
          below it can be moved by a re-render. */}
      <div className="row" style={{ marginBottom: "var(--ds-space-2)" }}>
        <span className="ds-label" style={{ flex: 1 }}>{battingTeamName} batting</span>
        {phase === null ? null : (
          <span className="ds-tag" style={{ background: "var(--ds-cat-teal-fill)", color: "var(--ds-cat-teal-ink)" }}>
            {phaseLabel(phase)}
          </span>
        )}
      </div>

      <div className="row" style={{ alignItems: "baseline", gap: "var(--ds-space-3)" }}>
        <span className="ds-score">{formatScore(innings)}</span>
        <span style={{ fontSize: "var(--ds-text-lg)", color: "var(--ds-text-muted)" }}>
          {formatOvers(header.overs)}
          {innings.oversAvailable === null ? "" : ` / ${innings.oversAvailable}`} ov
        </span>
        <span className="ds-caption" style={{ marginLeft: "auto", textAlign: "right" }}>
          CRR <b style={{ color: "var(--ds-text)" }}>{formatRate(header.currentRunRate)}</b>
          {header.target === null ? null : (
            <>
              <br />
              RRR <b style={{ color: "var(--ds-text)" }}>{formatRate(header.requiredRunRate)}</b>
            </>
          )}
        </span>
      </div>

      <InningsProgress completed={header.overs.completed} total={innings.oversAvailable} />

      {header.target === null ? null : (
        <div className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>
          {header.runsRequired === 0
            ? "Target reached"
            : `Need ${header.runsRequired} from ${header.ballsRemaining} balls`}
        </div>
      )}

      {/* Batter data rows */}
      {[header.striker, header.nonStriker].map((line, index) => (
        <BatterCard
          key={line?.playerId ?? index}
          line={line}
          striker={index === 0}
          name={name}
        />
      ))}

      {/*
        The partnership. Reserved whether or not there is one, so the bowler card below does
        not jump up a row at the fall of a wicket — the one moment the header is being read
        hardest.
      */}
      <div className="row" style={{ marginTop: "var(--ds-space-2)" }}>
        {partnership === null ? (
          <span className="ds-label">Partnership</span>
        ) : (
          <>
            <span className="ds-label">Partnership</span>
            <span className="ds-caption" style={{ fontVariantNumeric: "tabular-nums" }}>
              <b>{partnership.runs}</b> ({partnership.balls})
            </span>
          </>
        )}
      </div>

      <div className="row" style={{ marginTop: "var(--ds-space-3)", paddingTop: "var(--ds-space-3)", borderTop: "1px solid var(--ds-border)" }}>
        <span className="ds-label" style={{ flex: "none" }}>Bowling</span>
        <b style={{ flex: 1, marginLeft: "var(--ds-space-2)" }}>
          {header.bowler === null ? <i>bowler to come</i> : name(header.bowler.playerId)}
        </b>
        <span className="ds-caption" style={{ fontVariantNumeric: "tabular-nums" }}>
          {header.bowler === null ? "" : formatBowling(header.bowler)}
        </span>
      </div>

      {/* Badges for free hit, interruption, anomalies */}
      {(header.freeHit || innings.interrupted || state.anomalies.length > 0) && (
        <div className="row" style={{ marginTop: "var(--ds-space-3)", gap: "var(--ds-space-2)" }}>
          {/*
            A free hit has to be impossible to miss: the batter cannot be dismissed on it
            except by a run out, and a scorer who misses the badge will offer a dismissal
            the screen then refuses, which reads as a broken app rather than as the law.
          */}
          {header.freeHit ? <span className="ds-tag" style={{ background: "var(--ds-cat-teal-fill)", color: "var(--ds-cat-teal-ink)" }}>Free hit</span> : null}
          {innings.interrupted ? <span className="ds-tag" style={{ background: "var(--ds-cat-amber-fill)", color: "var(--ds-cat-amber-ink)" }}>Interrupted</span> : null}
          {/* Anomalies are surfaced, never hidden — an unresolved one blocks completion
              (ADR-0011), so finding out at the end of the match would be too late. */}
          {state.anomalies.length > 0 ? (
            <span className="ds-tag" style={{ background: "var(--ds-cat-magenta-fill)", color: "var(--ds-cat-magenta-ink)" }}>{state.anomalies.length} to review</span>
          ) : null}
        </div>
      )}
    </article>
  );
}

/**
 * One batter at the crease, as four fields rather than a sentence.
 *
 * The striker's card takes a teal left border. Which of the two is on strike is the fact a
 * scorer checks most often and the one that changes most often, so it gets a marker that is
 * visible without reading — the `*` beside the name stays as well, because a colour is not
 * available to everyone.
 */
function BatterCard({
  line,
  striker,
  name,
}: {
  readonly line: BattingLine | null;
  readonly striker: boolean;
  readonly name: (playerId: PlayerId) => string;
}): React.ReactElement {
  if (line === null) {
    return (
      <div className={`ds-datarow${striker ? " ds-datarow--active" : ""}`} style={{ marginTop: "var(--ds-space-4)" }}>
        <span className="ds-datarow__name">
          <i>{striker ? "striker" : "non-striker"} to come</i>
        </span>
      </div>
    );
  }
  return (
    <div className={`ds-datarow${line.isStriker ? " ds-datarow--active" : ""}`} style={line.isStriker ? { marginTop: "var(--ds-space-4)" } : undefined}>
      <span className="ds-datarow__name">
        {name(line.playerId)}
        {strikerMark(line)}
        {line.isStriker ? <span className="ds-caption"> striker</span> : null}
      </span>
      <span className="ds-datarow__value">{line.runs}</span>
      <span className="ds-datarow__sub">({line.balls})</span>
    </div>
  );
}

/**
 * How far through the innings this is, as a bar.
 *
 * The same fact as `(12.4/20)` beside the score, which is why the bar carries no text of its
 * own and is hidden from screen readers — a second announcement of the over count would be
 * noise. It is here for the glance: a proportion is read faster as a length than as a pair
 * of numbers, and "are we nearly done" is asked at every drinks break.
 *
 * Absent for a timed format, which has no proportion to show.
 */
function InningsProgress({
  completed,
  total,
}: {
  readonly completed: number;
  readonly total: number | null;
}): React.ReactElement | null {
  if (total === null || total <= 0) return null;
  const share = Math.min(1, Math.max(0, completed / total));
  return (
    <div className="ds-progress ds-progress--thin" aria-hidden="true" style={{ marginTop: "var(--ds-space-3)" }}>
      <span className="ds-progress__fill" style={{ width: `${(share * 100).toFixed(1)}%` }} />
    </div>
  );
}
